import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Total, { INVOICE_TOTAL_FIELDS } from "./InvoiceTotal";
import SaleItem from "./SaleItem";
import { BillTo } from "./BillTo";
import COLORS from "../colors";
import {
  deepFreeze,
  formatCurrency,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { cloneDeep, compact, every, get, isEmpty, map, sortBy } from "lodash";
import { diff } from "deep-object-diff";

export const CREDIT_MEMO_FIELDS = {
  creditMemo: true,
  reason: "",
  appliedTo: [],
  // Map of invoice id -> dollar amount of this credit memo applied to
  // that invoice. Populated by `functions/invoices.js::touchInvoice`
  // when the credit memo is created / edited / voided and used to
  // reverse the exact per-invoice allocation on void/delete so that
  // invoice balances and sale balance can't drift even if other
  // activity occurs between creation and reversal.
  allocation: {},
  billTo: null,
  amount: null,
  saleItems: {},
  saleLabors: [],
  miscItems: [],
  tax: null,
  total: null,
  invoiceDate: null,
  voidedDate: null,
  voidedReason: "",
  locked: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(CREDIT_MEMO_FIELDS);

export const CREDIT_MEMO_STATUS_TYPES = {
  open: {
    order: 1,
    label: "Open",
    icon: "credit_memo",
    color: "warning",
  },
  closed: {
    order: 2,
    label: "Closed",
    icon: "paid",
    color: "positive",
  },
  voided: {
    order: 3,
    label: "Voided",
    icon: "cancel",
    color: "negative",
  },
};
deepFreeze(CREDIT_MEMO_STATUS_TYPES);

export function computeCreditMemoTotal({ items, tax }) {
  const total = cloneDeep(INVOICE_TOTAL_FIELDS);

  if (tax && tax > 1) {
    tax /= 100;
  } else {
    tax = 0;
  }

  let taxableSubtotal = 0;
  items.forEach((item) => {
    const quantity =
      !item.itemType || item.itemType === "material" ? item.quantity : 1;
    const itemPrice = (item.price || 0) * quantity;
    taxableSubtotal +=
      tax && !(item.notax ?? item.itemType === "labor") ? itemPrice : 0;
    const category = item.category || "Other";
    if (!(category in total.categories)) {
      total.categories[category] = 0;
    }
    total.categories[category] += itemPrice;
    if (item.itemType === "material") {
      total.items += quantity;
    }
    total.subtotal += itemPrice;
  });

  // Clean up categories
  if (
    "Other" in total.categories &&
    Object.keys(total.categories).length === 1
  ) {
    total.categories = {};
  } else {
    Object.keys(total.categories).forEach((category) => {
      total.categories[category] = roundPrice(-total.categories[category]);
    });
  }

  total.tax = roundPrice(-(tax * taxableSubtotal));
  total.subtotal = roundPrice(-total.subtotal);
  total.price = roundPrice(total.subtotal + total.tax);
  total.balance = total.price;
  total.due = total.price;
  return total;
}

export default class CreditMemo extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    data = sanitize(data, CREDIT_MEMO_FIELDS);

    // Convert to Date
    data.invoiceDate = timestampToDate(data.invoiceDate);
    data.voidedDate = timestampToDate(data.voidedDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.projectID = projectID;
    this.saleID = saleID;
    this.id = id;
    this.permissions = Object.freeze({
      view: CreditMemo.canBeViewedBy,
      create: CreditMemo.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });

    // Defaults
    this.data.billTo = this.billTo.data;
    this.data.total = this.total.data;
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      !this.data.voidedDate &&
      !this.data.locked &&
      sale &&
      (!sale.data.locked || sale.data.canceled) &&
      user.isEmployee
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      !this.data.locked &&
      sale &&
      (!sale.data.locked || sale.data.canceled) &&
      (user.isAdmin || user.id === this.data.createdBy)
    );
  }

  // Database

  get path() {
    return CreditMemo.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/invoices`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(creditMemo) {
    return creditMemo.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    if (!data.creditMemo) return null;
    return new CreditMemo(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    return {
      reason: [(a) => Boolean(a) || "required"],
      invoiceDate: [(a) => Boolean(a) || "required"],
      appliedTo: [
        (a) =>
          !this.hasNonVoidedInvoices ||
          (Array.isArray(a) && a.length > 0) ||
          "required",
      ],
    };
  }

  get hasNonVoidedInvoices() {
    return Object.values(store.state.main.invoices).some(
      (invoice) =>
        invoice.projectID === this.projectID &&
        invoice.saleID === this.saleID &&
        !invoice.data.voidedDate
    );
  }

  get hasItems() {
    return this.items.length > 0;
  }

  get isValid() {
    return (
      (this.hasItems || (this.data.amount !== null && this.data.amount > 0)) &&
      every(this.rules, (rules, key) =>
        rules.every((rule) => rule(get(this.data, key)) === true)
      )
    );
  }

  computeTotal() {
    if (this.hasItems) {
      const items = this.items.map((item) => ({
        ...item.data,
        itemType: item.itemType,
      }));
      return computeCreditMemoTotal({ items, tax: this.data.tax });
    }
    // Manual amount
    const amount = this.data.amount || 0;
    let tax = this.data.tax;
    if (tax && tax > 1) tax /= 100;
    else tax = 0;
    const taxAmount = roundPrice(amount * tax);
    const total = cloneDeep(INVOICE_TOTAL_FIELDS);
    total.subtotal = roundPrice(-amount);
    total.tax = roundPrice(-taxAmount);
    total.price = roundPrice(total.subtotal + total.tax);
    total.balance = total.price;
    total.due = total.price;
    return total;
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(CreditMemo.sanitize(cloneDeep(this.data)), new CreditMemo().data)
      )
    );
  }

  isEqual(other) {
    return (
      this.id === other.id &&
      this.projectID === other.projectID &&
      this.saleID === other.saleID &&
      isEmpty(
        diff(
          CreditMemo.sanitize(cloneDeep(this.data)),
          CreditMemo.sanitize(cloneDeep(other.data))
        )
      )
    );
  }

  static sanitize(data) {
    return data;
  }

  // Formatting

  get project() {
    return store.state.main.projects[this.projectID];
  }

  get sale() {
    return store.state.main.sales[this.fullSaleID];
  }

  get fullSaleID() {
    return this.projectID + this.saleID;
  }

  get billTo() {
    return this.data.billTo ? new BillTo(this.data.billTo) : new BillTo();
  }

  get sortedCategories() {
    const categories = [
      ...new Set(this.items.map((item) => item.category || "Other")),
    ];
    if (categories.length > 1 || categories[0] !== "Other") {
      return categories;
    }
    return [];
  }

  get total() {
    return new Total(this.data.total);
  }

  get saleItems() {
    return sortBy(
      compact(
        map(this.data.saleItems, (quantity, id) => {
          let item = store.state.main.saleItems[id];
          return item
            ? new SaleItem(this.projectID, this.saleID, id, {
                ...item.data,
                quantity,
                price: item.data.price,
              })
            : null;
        })
      ),
      "line"
    );
  }

  get saleLabors() {
    return sortBy(
      compact(
        this.data.saleLabors.map(
          (id) => store.state.main.saleLabors[id] || null
        )
      ),
      "line"
    );
  }

  get miscItems() {
    return sortBy(
      compact(
        this.data.miscItems.map((id) => store.state.main.miscItems[id] || null)
      ),
      "line"
    );
  }

  get items() {
    return sortBy(
      [...this.saleItems, ...this.saleLabors, ...this.miscItems],
      "line"
    );
  }

  get maxAmount() {
    const invoices = this.appliedToInvoices;
    if (!invoices.length) return 0;
    return invoices.reduce(
      (sum, inv) =>
        sum +
        Math.abs(
          (inv.data.total.subtotal || 0) - (inv.data.total.discount || 0)
        ),
      0
    );
  }

  get amountExceedsInvoiced() {
    if (!this.appliedToInvoices.length) return false;
    const amount = this.hasItems
      ? Math.abs(this.computeTotal().subtotal)
      : this.data.amount || 0;
    return amount > this.maxAmount + 0.01;
  }

  get isFullInvoiceAmount() {
    const invoices = this.appliedToInvoices;
    if (!invoices.length || this.items.length) return false;
    return (
      this.data.amount != null &&
      Math.abs(this.data.amount - this.maxAmount) < 0.01
    );
  }

  get effectiveItems() {
    if (this.items.length) return this.items;
    if (this.isFullInvoiceAmount && this.appliedToInvoices.length === 1) {
      return this.appliedToInvoices[0].items || [];
    }
    return [];
  }

  get appliedToInvoices() {
    const ids = this.data.appliedTo || [];
    return compact(ids.map((id) => store.state.main.invoices[id]));
  }

  get appliedToInvoice() {
    return this.appliedToInvoices[0] || null;
  }

  // Dollars of this credit memo touchInvoice has placed on invoices.
  get appliedAmount() {
    const allocation = this.data.allocation || {};
    return roundPrice(
      Object.values(allocation).reduce((sum, n) => sum + (Number(n) || 0), 0)
    );
  }

  // Dollars of this credit memo already refunded to the customer. A
  // "credit-refund" transaction posts onto the memo's own `total.payments`
  // (functions/transactions.js addToBalance), negative for a refund.
  get refundedAmount() {
    return roundPrice(Math.abs(Number(this.data.total?.payments) || 0));
  }

  // Dollars of this credit memo neither on an invoice nor refunded: the most
  // a refund can take from it. Justin (2026-10-20): "you cannot receive more
  // than a credit memo balance as a refund." Voided memos have nothing left.
  get unappliedAmount() {
    if (this.data.voidedDate) return 0;
    const amount = Math.abs(
      Number(this.data.total?.price || this.data.amount) || 0
    );
    return roundPrice(
      Math.max(0, amount - this.appliedAmount - this.refundedAmount)
    );
  }

  get status() {
    if (this.data.voidedDate) {
      return "voided";
    }

    const creditMemoBalance = this.data?.total?.balance;
    if (Number.isFinite(creditMemoBalance) && creditMemoBalance >= -0.01) {
      return "closed";
    }

    const saleBalance = this.sale?.data?.total?.balance;
    if (Number.isFinite(saleBalance) && saleBalance >= -0.01) {
      return "closed";
    }

    return "open";
  }

  get statusType() {
    const type = CREDIT_MEMO_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get totalFormatted() {
    return formatCurrency(-this.data.total.price);
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  toString() {
    return this.id;
  }
}
